import React, { useState } from 'react';
import { View, Text, Pressable, Platform } from 'react-native';
import { BackIcon, SaveIcon, SubmitIcon, ForwardIcon, CompleteIcon } from '../StandupIcons';
import { StyleSheet } from 'react-native';
import FontFamily from '../../../../constants/fonts';
import LHModalPop from '../../../../components/LHModalPop';
import logger from '../../../../utils/logger';

interface HeaderSectionProps {
  standupType: string;
  standupDate: string;
  onBackPress: () => void;
  backToEditPress: () => void;
  backtoDashboard: () => void;
  onSavePress: () => void;
  onContinuePress: () => void;
  onCompletePress?: () => void;
  onSubmitPress: () => void;
  activeTab: string;
  favoriteSparcCards: string[];
  favoriteLighthouseCards: string[];
  favoriteLighthouseTasks: string[];
  selectedKpiCards?: any[]; // Selected KPIs from Redux (includes both SPARC and Lighthouse)
  isViewMode?: boolean;
  standupItemStatus?: string;
  isSubmitted?: boolean;
  isBackToEditInViewMode?: boolean;
  disableSaveAndContinue?: boolean;
}

// Generate standup title from type and date
const generateStandupTitle = (standupType: string): string => {
  // Map short codes to full radio button text
  const standupTypeMap: { [key: string]: string } = {
    'weekly': 'Weekly Stand-up',
    'weekly_lt': 'Weekly Stand-up',
    'weekly-lt': 'Weekly Stand-up',
    'daily_lt': 'Daily Stand-up',
    'daily-lt': 'Daily Stand-up',
    'daily_frontline': 'Daily Frontline Stand-up',
    'daily-frontline': 'Daily Frontline Stand-up',
    'daily_flt': 'Daily Frontline Stand-up',
  };

  // Get full standup type text (preserve original if not in map)
  const fullStandupType = standupTypeMap[standupType.toLowerCase()] || standupType;

  if (!fullStandupType) {
    return 'Daily Stand-up';
  }
  return fullStandupType;
};

export const HeaderSection: React.FC<HeaderSectionProps> = ({
  standupType,
  standupDate,
  onBackPress,
  backToEditPress,
  backtoDashboard,
  onSavePress,
  onContinuePress,
  onCompletePress,
  onSubmitPress,
  activeTab,
  favoriteSparcCards,
  favoriteLighthouseCards,
  favoriteLighthouseTasks,
  selectedKpiCards = [],
  isViewMode = false,
  standupItemStatus,
  isSubmitted = false,
  isBackToEditInViewMode = false,
  disableSaveAndContinue = false,
}) => {
  const title = generateStandupTitle(standupType);
  const [isBackHovered, setIsBackHovered] = useState(false);
  const [isSaveHovered, setIsSaveHovered] = useState(false);
  const [isContinueHovered, setIsContinueHovered] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  // Handle showing completion confirmation modal (only in view mode)
  const handleShowCompletionModal = () => {
    if (isViewMode || isSubmitted) {
      // View mode: show confirmation modal
      setShowCompletionModal(true);
    } else {
      // Create/Edit mode: submit directly without modal
      if (onCompletePress) {
        onCompletePress();
      }
    }
  };

  const backToEditPressHandle = async () => {
    if (backToEditPress) {
      setIsBackHovered(false);
      backToEditPress();
    }
  }
  
  const handlesubmitCompletion = () => {
    // if (isViewMode) {
    //   // View mode: show confirmation modal
    //   setShowCompletionModal(true);
    // } else {
    // Create/Edit mode: submit directly without modal
    if (onSubmitPress) {
      onSubmitPress();
    }
    //}
  }

  // Handle completion confirmation
  const handleConfirmCompletion = () => {
    setShowCompletionModal(false);
    if (onCompletePress) {
      onCompletePress();
    }
  };

  // Handle completion cancellation
  const handleCancelCompletion = () => {
    setShowCompletionModal(false);
  };

  // Calculate if Continue button should be enabled based on tab and favorites
  const isContinueEnabled = () => {
    switch (activeTab) {
      case 'review-performance':
        // Enable if at least one KPI card is in the Selected KPIs section (managed by Redux)
        // selectedKpiCards contains both SPARC KPIs and Lighthouse metrics
        console.log('🔍 Continue button check:', {
          activeTab,
          selectedKpiCardsCount: selectedKpiCards.length,
          selectedKpiCards,
          result: selectedKpiCards.length > 0
        });
        return selectedKpiCards.length > 0;
      case 'key-activities':
        return true;
      case 'news':
        // Always enable for NewsTab
        return true;
      case 'review-progress':
        // Always enable for ReviewProgressTab (Submit button)
        return true;
      default:
        return true;
    }
  };

  const continueEnabled = isContinueEnabled();

  // Get tooltip text based on active tab
  const getTooltipText = () => {
    switch (activeTab) {
      case 'review-performance':
        return 'Select KPIs to continue';
      case 'key-activities':
        return 'Select tasks to continue';
      default:
        return '';
    }
  };
  const normalizedStatus = standupItemStatus ? standupItemStatus.toLowerCase() : '';
  return (
    <>
      <View style={styles.headerSection}>
        {/* Title on the left */}
        <Text style={styles.headerTitle}>{title}</Text>

        {/* Buttons on the right */}
        <View style={styles.headerButtons}>
          {(isViewMode || isSubmitted) && !isBackToEditInViewMode ? (
            // View mode: if completed, only show Back to Dashboard; otherwise show Back to Edit and Submit
            <>
              {(['completed', 'incomplete'].includes(normalizedStatus)) ? (
                // Completed standup: only show Back to Dashboard button
                <Pressable
                  style={({ pressed }) => [
                    styles.backButton,
                    isBackHovered && styles.buttonHovered,
                    pressed && styles.buttonPressed
                  ]}
                  onPress={backtoDashboard}
                  onHoverIn={() => setIsBackHovered(true)}
                  onHoverOut={() => setIsBackHovered(false)}
                >
                  <BackIcon color={isBackHovered ? "#fff" : "#004895"} />
                  <Text style={[styles.backButtonText, isBackHovered && styles.buttonTextHovered]}>
                    Back to Dashboard
                  </Text>
                </Pressable>
              ) : (
                // Non-completed standup: show Back to Edit and Submit buttons
                <>
                  {/* Back to Edit Button */}
                {normalizedStatus !== 'incomplete' && (
                  <Pressable 
                    style={({ pressed }) => [
                      styles.backToEditButton,
                      isBackHovered && styles.backToEditButton,
                      pressed && styles.backToEditButton
                    ]}
                    onPress={backToEditPressHandle}
                    onHoverIn={() => setIsBackHovered(true)}
                    onHoverOut={() => setIsBackHovered(false)}
                  >
                    <BackIcon color={isBackHovered ? "#004895" : "#004895"} />
                    <Text style={[styles.backToEditButtonText, isBackHovered && styles.backToEditButtonText]}>
                      Back to Edit
                    </Text>
                  </Pressable>
                )}

                  {/* Submit Button */}
                  <Pressable
                    style={({ pressed }) => [
                      styles.continueButton,
                      isContinueHovered && styles.continueButtonHovered,
                      pressed && styles.buttonPressed,
                    ]}
                    onPress={handleShowCompletionModal}
                    onHoverIn={() => setIsContinueHovered(true)}
                    onHoverOut={() => setIsContinueHovered(false)}
                  >
                    <Text style={[styles.continueButtonText, isContinueHovered && styles.continueButtonTextHovered]}>
                      Complete
                    </Text>
                    <CompleteIcon />
                  </Pressable>
                </>
              )}
            </>
          ) : (
            // Normal mode: show all buttons
            <>
              {/* Back Button */}
              <Pressable
                style={({ pressed }) => [
                  styles.backButton,
                  isBackHovered && styles.buttonHovered,
                  pressed && styles.buttonPressed
                ]}
                onPress={onBackPress}
                onHoverIn={() => setIsBackHovered(true)}
                onHoverOut={() => setIsBackHovered(false)}
              >
                <BackIcon color={isBackHovered ? "#fff" : "#004895"} />
                <Text style={[
                  styles.backButtonText,
                  isBackHovered && styles.buttonTextHovered
                ]}>Back</Text>
              </Pressable>

              {/* Save for Later Button */}
              <Pressable
                style={({ pressed }) => [
                  styles.saveButton,
                  disableSaveAndContinue && styles.saveButtonDisabled,
                  !disableSaveAndContinue && isSaveHovered && styles.buttonHovered,
                  pressed && !disableSaveAndContinue && styles.buttonPressed
                ]}
                onPress={disableSaveAndContinue ? undefined : onSavePress}
                disabled={disableSaveAndContinue}
                onHoverIn={() => !disableSaveAndContinue && setIsSaveHovered(true)}
                onHoverOut={() => !disableSaveAndContinue && setIsSaveHovered(false)}
              >
                <SaveIcon color={disableSaveAndContinue ? "#B1B1B1" : (isSaveHovered ? "#fff" : "#004895")} />
                <Text style={[
                  styles.saveButtonText,
                  disableSaveAndContinue && styles.saveButtonTextDisabled,
                  !disableSaveAndContinue && isSaveHovered && styles.buttonTextHovered
                ]}>Save for Later</Text>
              </Pressable>

              {/* Continue/Submit Button */}
              {activeTab === 'review-progress' ? (
                <Pressable
                  style={[
                    styles.continueButton,
                    disableSaveAndContinue && styles.continueButtonDisabled,
                    !disableSaveAndContinue && isContinueHovered && styles.continueButtonHovered
                  ]}
                  onPress={disableSaveAndContinue ? undefined : handlesubmitCompletion}
                  disabled={disableSaveAndContinue}
                  onHoverIn={() => !disableSaveAndContinue && setIsContinueHovered(true)}
                  onHoverOut={() => !disableSaveAndContinue && setIsContinueHovered(false)}
                >
                  <SubmitIcon />
                  <Text style={[
                    styles.continueButtonText,
                    disableSaveAndContinue && styles.continueButtonTextDisabled,
                    !disableSaveAndContinue && isContinueHovered && styles.continueButtonTextHovered
                  ]}>Submit</Text>
                </Pressable>
              ) : (
                <View style={styles.continueButtonWrapper}>
                  <Pressable
                    style={[
                      styles.continueButton,
                      (!continueEnabled || disableSaveAndContinue) && styles.continueButtonDisabled,
                      continueEnabled && !disableSaveAndContinue && isContinueHovered && styles.continueButtonHovered
                    ]}
                    onPress={(continueEnabled && !disableSaveAndContinue) ? onContinuePress : undefined}
                    disabled={!continueEnabled || disableSaveAndContinue}
                    onHoverIn={() => continueEnabled && !disableSaveAndContinue && setIsContinueHovered(true)}
                    onHoverOut={() => continueEnabled && !disableSaveAndContinue && setIsContinueHovered(false)}
                    {...(Platform.OS === 'web' && (!continueEnabled || disableSaveAndContinue)
                      ? {
                        onMouseEnter: () => setShowTooltip(true),
                        onMouseLeave: () => setShowTooltip(false),
                      }
                      : {})}
                  >
                    <Text style={[
                      styles.continueButtonText,
                      (!continueEnabled || disableSaveAndContinue) && styles.continueButtonTextDisabled,
                      continueEnabled && !disableSaveAndContinue && isContinueHovered && styles.continueButtonTextHovered
                    ]}>Continue</Text>
                    <ForwardIcon color={(!continueEnabled || disableSaveAndContinue) ? "#9E9E9E" : (continueEnabled && !disableSaveAndContinue && isContinueHovered ? "#fff" : "#ABABAB")} />
                  </Pressable>
                  {showTooltip && (!continueEnabled || disableSaveAndContinue) && Platform.OS === 'web' && (
                    <View style={styles.tooltip}>
                      <Text style={styles.tooltipText}>{getTooltipText()}</Text>
                    </View>
                  )}
                </View>
              )}
            </>
          )}
        </View>
      </View>

      {/* Completion Confirmation Modal */}
      <LHModalPop
        visible={showCompletionModal}
        onClose={handleCancelCompletion}
        title="Confirm Submission"
        description="Once Completed, a stand up cannot be modified. Are you sure you would like to continue?"
        showFooter={true}
        primaryButtonText="Complete"
        secondaryButtonText="Cancel"
        onPrimaryPress={handleConfirmCompletion}
        onSecondaryPress={handleCancelCompletion}
        showCloseButton={true}
      />
    </>
  );
};

const styles = StyleSheet.create({
  headerSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 24,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: '#2d2d2d',
    fontFamily: FontFamily.bold,
  },
  headerButtons: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'center',
  },
  backToEditButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 8,
    backgroundColor: 'none',
    gap: 8,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 8,
    borderRadius: 24,
    backgroundColor: '#fff',
    borderWidth: 2,
    borderColor: '#004895',
    gap: 8,
  },
  backToEditButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#004895'
  },
  backButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#004895',
  },
  saveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 8,
    borderRadius: 24,
    backgroundColor: '#fff',
    borderWidth: 2,
    borderColor: '#004895',
    gap: 8,
  },
  saveButtonText: {
    fontFamily: FontFamily.medium,
    fontSize: 14,
    fontWeight: '500',
    color: '#004895',
  },
  saveButtonDisabled: {
    backgroundColor: '#D9D9D9',
    borderColor: '#D9D9D9',
    opacity: 0.6,
  },
  saveButtonTextDisabled: {
    color: '#ADADAD',
  },
  continueButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 24,
    backgroundColor: '#004895',
    borderWidth: 0,
    gap: 8,
  },
  continueButtonDisabled: {
    backgroundColor: '#D9D9D9',
    borderColor: '#D9D9D9',
    opacity: 0.6,
  },
  continueButtonHovered: {
    backgroundColor: '#003366',
    borderColor: 'transparent',
  },
  continueButtonText: {
    fontFamily: FontFamily.medium,
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  continueButtonTextDisabled: {
    color: '#ADADAD',
  },
  continueButtonTextHovered: {
    color: '#FFFFFF',
  },
  buttonHovered: {
    backgroundColor: '#004895',
  },
  buttonTextHovered: {
    color: '#fff',
  },
  buttonPressed: {
    opacity: 0.8,
  },
  continueButtonWrapper: {
    position: 'relative',
  },
  tooltip: {
    position: 'absolute',
    bottom: '100%',
    left: '50%',
    transform: [{ translateX: '-50%' }],
    marginBottom: 8,
    backgroundColor: '#1C1C1C',
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 8,
    minWidth: 150,
    ...(Platform.OS === 'web' && {
      // @ts-ignore - web-only styles
      transform: 'translateX(-50%)',
    }),
  },
  tooltipText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '400',
    fontFamily: Platform.OS === 'ios' ? 'System' : 'sans-serif',
    textAlign: 'center',
  },
});